import { Container, FormFieldHeading } from '@/components/Elements';
import ApplicationHeading from '@/components/Elements/Heading/ApplicationHeading';
import {
  DatePickerField,
  InputField,
  ListOptionsField,
  RadioGroupField,
  SelectField,
} from '@/components/Form';
import StepperFormLayout from '@/components/Layout/StepperFormLayout';
import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TAX_INCOME_OPTIONS } from '../constant/optionList';
import { AGREE_OPTIONS } from '@/constant/options';
import { usePostLeadDataMutation } from '@/pages/onboarding/api/onboardAPI';
import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LodgeReturnRoutes } from '../constant/routes';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Grid,
  message,
  Row,
  theme,
  Typography,
} from 'antd';
import {
  useGetApplicationDetailQuery,
  usePostLodgeReturnMutation,
  usePutLodgeReturnMutation,
} from '@/pages/tax/apis/lodgeReturnAPI';
import { yupResolver } from '@hookform/resolvers/yup';
import { lodgeReturnApplicationSchema } from '../schema/lodgeReturnApplicationSchema';
import { LodgeReturnApplicationFormType } from '../types';
import { useFetchUserDataQuery } from '@/store/apis/userApi';
import dayjs from 'dayjs';
const { useBreakpoint } = Grid;
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import PhoneInputField from '@/components/Form/PhoneInputField';
import { useCountryOptions } from '@/hooks/useCountryOptions';

const LodgeReturnApplication = () => {
  const { id } = useParams();
  const total = 2;
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const countryOptions = useCountryOptions();
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<LodgeReturnApplicationFormType>({
    resolver: yupResolver(lodgeReturnApplicationSchema),
  });

  const { data: profileData } = useFetchUserDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: applicationDetail } = useGetApplicationDetailQuery(
    {
      application_id: id!,
    },
    { skip: !id, refetchOnMountOrArgChange: true },
  );

  const { data: userData } = useFetchUserDataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [postLodgeReturn, { isLoading: postPurchaseRequestLoading }] =
    usePostLodgeReturnMutation();

  const [putLodgeReturn, { isLoading: putPurchaseRequestLoading }] =
    usePutLodgeReturnMutation();

  const onSubmit = (data: any) => {
    const agreeToTerms = getValues('agreeToTerms');
    if (!agreeToTerms) {
      message.error('Please agree to the terms.');
      return;
    }
    const { taxYear, ...rest } = data;
    const payload: {
      data: LodgeReturnApplicationFormType;
      tax_year: number;
      application_id?: number;
    } = {
      data: rest,
      tax_year: data.taxYear,
    };

    payload.data.dateOfBirth = window.btoa(
      dayjs(payload.data.dateOfBirth).format('YYYY-MM-DD'),
    );

    payload.data.australianTaxFileNumber = window.btoa(
      payload.data.australianTaxFileNumber,
    );

    if (id) {
      payload.application_id = parseInt(id);
      putLodgeReturn(payload)
        .unwrap()
        .then((res) => {
          displaySuccess('Successfully submitted details.');
          navigate('/tax/lodge-return');
        })
        .catch((err) => {
          displayError(err);
        });
    } else {
      postLodgeReturn(payload)
        .unwrap()
        .then((res) => {
          displaySuccess('Successfully submitted details.');
          navigate('/tax/lodge-return');
        })
        .catch((err) => {
          displayError(err);
        });
    }
  };
  const applicationDetailData = applicationDetail?.data
    ?.data as LodgeReturnApplicationFormType;

  useEffect(() => {
    // Populate from user profile (fallback)
    if (profileData) {
      setValue('australianTaxFileNumber', profileData?.data?.tfnNumber ?? '');
    }

    // Populate from applicationDetail if editing
    if (applicationDetail) {
      setValue('firstName', applicationDetailData?.firstName);
      setValue('lastName', applicationDetailData?.lastName);
      setValue('email', applicationDetailData?.email);
      setValue('phone', applicationDetailData?.phone);
      setValue(
        'australianTaxFileNumber',
        applicationDetailData?.australianTaxFileNumber ??
          profileData?.data?.tax_file_number,
      );
      setValue('dateOfBirth', applicationDetailData.dateOfBirth);
    } else if (userData?.data) {
      setValue('firstName', userData.data.firstName ?? '');
      setValue('lastName', userData.data.lastName ?? '');
      setValue('email', userData.data.email ?? '');
      setValue('phone', userData.data.phone ?? '');
      setValue('country', userData.data.country ?? '');
    }
  }, [applicationDetail, userData, profileData, setValue]);

  const handleFormSubmit = () => {
    if (submitButtonRef.current) {
      submitButtonRef.current.click();
    }
  };
  const screen = useBreakpoint();

  console.log(userData?.data);

  return (
    <StepperFormLayout
      showBackButton={false}
      handleSubmit={handleFormSubmit}
      nextBtnText="Submit Application"
    >
      <Container width="md">
        <Row justify="center">
          <Col>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Row gutter={[8, 8]}>
                <Col>
                  <Typography.Title level={3}>
                    Free ATO Tax Health
                  </Typography.Title>
                  <Typography.Paragraph>
                    Been away from Australia and missed your tax return — or
                    thought you didn’t need to? We’re here to help! Odin offers
                    a free tax health check to ensure your Australian taxes are
                    up-to-date. Just fill in the form, and we’ll review your tax
                    history with the ATO. Whether you have Australian income or
                    not, staying current with the ATO is essential. Once we’ve
                    completed your health check, you’ll receive a personalised
                    action plan. Let us help you manage your taxes —
                    stress-free!
                  </Typography.Paragraph>
                </Col>
                <Col xs={24}>
                  <Typography.Title level={2}>
                    Personal Details
                  </Typography.Title>
                </Col>
                <Col xs={24} md={12}>
                  <InputField
                    label="First Name"
                    name="firstName"
                    control={control}
                    size="large"
                    error={errors?.firstName?.message}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <InputField
                    label="Last Name"
                    name="lastName"
                    control={control}
                    size="large"
                    error={errors?.lastName?.message}
                  />
                </Col>
                <Col xs={24}>
                  <InputField
                    label="Email"
                    name="email"
                    control={control}
                    size="large"
                    error={errors?.email?.message}
                  />
                </Col>
                <Col xs={24}>
                  <PhoneInputField
                    label="Phone Number"
                    control={control}
                    name="phone"
                    error={errors.phone?.message ?? ''}
                  />
                </Col>
                <Col xs={24}>
                  <SelectField
                    label="Country Of Residence"
                    showSearch={true}
                    options={countryOptions}
                    name="country"
                    control={control}
                    size="large"
                    placeholder="Select your country"
                    error={errors.country?.message ?? ''}
                    required={true}
                  />
                </Col>
                <Col xs={24}>
                  <Typography.Title level={3}>Tax Details</Typography.Title>
                  <Typography.Text>
                    Tax Information Information required to add you to our tax
                    agent portal:
                  </Typography.Text>
                </Col>
                <Col xs={24} md={12}>
                  <InputField
                    name="australianTaxFileNumber"
                    control={control}
                    type="password"
                    size="large"
                    label="Australian Tax File Number"
                    error={errors?.australianTaxFileNumber?.message}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <DatePickerField
                    control={control}
                    label="Date of Birth"
                    name="dateOfBirth"
                    size="large"
                    placeholder="Select Date of Birth"
                    error={errors?.dateOfBirth?.message}
                  />
                </Col>
                <Col xs={24}>
                  <Controller
                    name="agreeToTerms"
                    control={control}
                    defaultValue={false}
                    render={({ field }) => (
                      <div
                        style={{
                          padding: '16px',
                          border: '1px solid #d9d9d9',
                          borderRadius: '8px',
                          backgroundColor: '#fafafa',
                        }}
                      >
                        <Checkbox {...field} checked={field.value}>
                          <Typography.Text strong>
                            I authorise Odin Tax to act as my tax agent and
                            liaise with the ATO on my behalf.*
                          </Typography.Text>
                        </Checkbox>

                        <div style={{ marginTop: '8px', paddingLeft: '24px' }}>
                          <Typography.Text type="secondary">
                            By proceeding, you agree to receive our tax and
                            finance updates, and accept our{' '}
                            <a
                              href="https://www.odinmortgage.com/privacy-security/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Privacy Policy
                            </a>
                            .
                            <br />
                            <strong>Disclaimer:</strong> We will add you to our
                            tax agent portal after submission. You can request
                            removal at any time.
                          </Typography.Text>
                        </div>
                      </div>
                    )}
                  />
                </Col>

                <Col xs={24}>
                  <Button
                    ref={submitButtonRef}
                    type="primary"
                    htmlType="submit"
                    style={{ display: 'none' }}
                  >
                    Submit
                  </Button>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </StepperFormLayout>
  );
};

export default LodgeReturnApplication;

const Label = ({ label }: { label: string }) => {
  return (
    <Typography.Title
      level={5}
      style={{
        color: 'rgba(0, 0, 0, 0.85)', // Using a direct color value instead of token.colorTextLabel
        marginTop: '1rem',
      }}
    >
      {label}
    </Typography.Title>
  );
};
